import * as d3 from 'd3';

/**
 * One shared crosshair for the whole engine -- a vertical line spanning the
 * full chart height, plus a horizontal line + sliding price tag scoped to
 * whichever pane the cursor is currently over (price, or a specific
 * indicator subpane), plus a date tag sliding along the bottom axis.
 *
 * All DOM nodes are created once and updated via attr-only calls on every
 * mousemove -- never a redraw of chart content. This is the rule that
 * matters most once Ichimoku's cloud fill exists (the most expensive draw
 * call in the engine): crosshair movement must never trigger it.
 */
export function createCrosshair(root, { totalHeight, theme }) {
  const layer = root
    .append('g')
    .attr('class', 'crosshair-layer')
    .attr('pointer-events', 'none')
    .style('opacity', 0);

  const vLine = layer
    .append('line')
    .attr('y1', 0)
    .attr('y2', totalHeight)
    .attr('stroke', theme.crosshair.line)
    .attr('stroke-width', 1)
    .attr('stroke-dasharray', theme.crosshair.dasharray);

  const hLineGroup = layer.append('g');
  const hLine = hLineGroup
    .append('line')
    .attr('stroke', theme.crosshair.line)
    .attr('stroke-width', 1)
    .attr('stroke-dasharray', theme.crosshair.dasharray);

  const PRICE_TAG_W = 66;
  const PRICE_TAG_H = 20;
  const priceTag = hLineGroup.append('g');
  priceTag
    .append('rect')
    .attr('width', PRICE_TAG_W)
    .attr('height', PRICE_TAG_H)
    .attr('y', -PRICE_TAG_H / 2)
    .attr('rx', 3)
    .attr('fill', theme.crosshair.tagBackground);
  const priceTagText = priceTag
    .append('text')
    .attr('x', PRICE_TAG_W / 2)
    .attr('dominant-baseline', 'middle')
    .attr('text-anchor', 'middle')
    .attr('font-size', 11)
    .attr('font-weight', 600)
    .attr('font-family', theme.typography.fontFamily)
    .attr('fill', theme.crosshair.tagText)
    .style('font-variant-numeric', 'tabular-nums');

  const DATE_TAG_W = 90;
  const DATE_TAG_H = 20;
  const dateTag = layer.append('g');
  dateTag
    .append('rect')
    .attr('width', DATE_TAG_W)
    .attr('height', DATE_TAG_H)
    .attr('x', -DATE_TAG_W / 2)
    .attr('rx', 3)
    .attr('fill', theme.crosshair.tagBackground);
  const dateTagText = dateTag
    .append('text')
    .attr('dominant-baseline', 'middle')
    .attr('text-anchor', 'middle')
    .attr('font-size', 11)
    .attr('font-weight', 600)
    .attr('font-family', theme.typography.fontFamily)
    .attr('fill', theme.crosshair.tagText);

  return {
    moveTo({ mouseX, availableWidth, pane, valueY, priceLabel, dateLabel }) {
      layer.style('opacity', 1);
      vLine.attr('x1', mouseX).attr('x2', mouseX);

      hLineGroup.attr('transform', `translate(0,${pane.top})`);
      hLine.attr('x1', 0).attr('x2', availableWidth).attr('y1', valueY).attr('y2', valueY);
      priceTag.attr('transform', `translate(${availableWidth + 2},${valueY})`);
      priceTagText.attr('y', 0.5).text(priceLabel);

      dateTag.attr('transform', `translate(${mouseX},${totalHeight + 2})`);
      dateTagText.attr('y', DATE_TAG_H / 2 + 0.5).text(dateLabel);
    },
    hide() {
      layer.style('opacity', 0);
    },
  };
}

export const bisectDate = d3.bisector((d) => d.date).left;

export function findNearest(data, date) {
  const i = bisectDate(data, date, 1);
  const d0 = data[i - 1];
  const d1 = data[i];
  if (!d0) return d1;
  if (!d1) return d0;
  return date - d0.date > d1.date - date ? d1 : d0;
}
